import React from "react";
import { DataGrid, GridToolbarContainer, GridFooterContainer } from "@mui/x-data-grid";
import { injectIntl } from "react-intl";
import { FormControl, InputLabel, Select, MenuItem, Box, Typography } from "@mui/material";
import "./ProductPricesTable.css";

// Fix for browsers which dont have localeCompare
if (typeof String.prototype.localeCompare === "undefined") {
  String.prototype.localeCompare = (str) => {
    // eslint-disable-line no-extend-native
    return this === str ? 0 : this > str ? 1 : -1;
  };
}

class ProductPricesTable extends React.Component {
  CustomToolbar = () => {
    const { intl, unfilteredProducts, chainFilter, productFilter, onChainFilterChange, onProductFilterChange } = this.props;

    // Get unique chains for filter options from the FULL unfiltered data
    const uniqueChains = [...new Set(unfilteredProducts.map((p) => p.chain))].filter(Boolean).sort();

    // Build unique products keyed by productKey so same-named products from different sources appear separately.
    // Label: prepend Varumärke only when it is not already contained in the product name.
    const buildLabel = (manufacturer, name) => {
      if (!manufacturer || name.toLowerCase().includes(manufacturer.toLowerCase())) {
        return name;
      }
      return `${manufacturer} ${name}`;
    };
    const seenKeys = new Set();
    const uniqueProducts = unfilteredProducts
      .filter((p) => p.productKey && !seenKeys.has(p.productKey) && seenKeys.add(p.productKey))
      .map((p) => ({
        key: p.productKey,
        label: buildLabel(p.manufacturer, p.product),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'sv'));

    const chainLabel = intl.formatMessage({ id: "priceTable.chain" });
    const productLabel = intl.formatMessage({ id: "priceTable.product" });
    const storeFilterLabel = intl.formatMessage({ id: "store.storeFilter" });
    const ProductFilterLabel = intl.formatMessage({ id: "product.productsFilter" });

    return (
      <GridToolbarContainer sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" sx={{ ml: 0.5, fontWeight: "bold" }}>
            Filter
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth variant="standard" size="small">
                <InputLabel>{chainLabel}</InputLabel>
                <Select value={chainFilter} onChange={onChainFilterChange}>
                  <MenuItem value="">
                    <em>{storeFilterLabel}</em>
                  </MenuItem>
                  {uniqueChains.map((chain) => (
                    <MenuItem key={chain} value={chain}>
                      {chain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 300 }}>
              <FormControl fullWidth variant="standard" size="small">
                <InputLabel>{productLabel}</InputLabel>
                <Select value={productFilter} onChange={onProductFilterChange}>
                  <MenuItem value="">
                    <em>{ProductFilterLabel}</em>
                  </MenuItem>
                  {uniqueProducts.map((p) => (
                    <MenuItem key={p.key} value={p.key}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </GridToolbarContainer>
    );
  };

  CustomFooter = () => {
    const { intl, products } = this.props;
    const rowCount = products ? products.length : 0;
    return (
      <GridFooterContainer sx={{ justifyContent: "flex-end", paddingRight: "16px" }}>
        {intl.formatMessage({ id: "product.count" })} {rowCount}
      </GridFooterContainer>
    );
  };

  getLocaleText = () => {
    const { intl } = this.props;
    return {
      columnMenuLabel: intl.formatMessage({ id: "dataGrid.columnMenuLabel" }),
      columnMenuShowColumns: intl.formatMessage({ id: "dataGrid.columnMenuShowColumns" }),
      columnMenuFilter: intl.formatMessage({ id: "dataGrid.columnMenuFilter" }),
      columnMenuHideColumn: intl.formatMessage({ id: "dataGrid.columnMenuHideColumn" }),
      columnMenuUnsort: intl.formatMessage({ id: "dataGrid.columnMenuUnsort" }),
      columnMenuSortAsc: intl.formatMessage({ id: "dataGrid.columnMenuSortAsc" }),
      columnMenuSortDesc: intl.formatMessage({ id: "dataGrid.columnMenuSortDesc" }),
    };
  };

  render() {
    const { products, columns, height } = this.props;

    if (!products) {
      return <div />;
    }

    // Ensure every row has a unique id property for DataGrid
    const rows = products.map((row, index) => ({
      ...row,
      id: row.id || index,
    }));

    const tableHeight = height || "400px";

    return (
      <div style={{ height: tableHeight, width: "100%" }}>
        <DataGrid
          sx={{ border: "none" }}
          rows={rows}
          columns={columns}
          localeText={this.getLocaleText()}
          pageSize={-1}
          hideFooterPagination
          components={{
            Toolbar: this.CustomToolbar,
            Footer: this.CustomFooter,
          }}
          disableColumnMenu
          sortingOrder={["asc", "desc"]}
          initialState={{
            sorting: {
              sortModel: [{ field: "price", sort: "desc" }],
            },
          }}
        />
      </div>
    );
  }
}

export default injectIntl(ProductPricesTable);
